import { useContext } from 'react'

import styled from '@emotion/styled'

import { ArcadeIcon, NavigationBackIcon, NavigationCloseIcon } from '@arcade/web/icons/react'

import ArcadeButton from '../Arcade/Button/ArcadeButton'
import { Flex } from '../layout/Flex'
import { ThemeVariantProvider } from '../Provider'
import Text from '../Text/Text'
import { ArcadeModalActionContext } from './ArcadeModalActionContext'

const HeaderTitle = styled(Text)`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
`

const ModalHeaderRow = styled(Flex)<{
  enforceResponsiveMobileDialog?: boolean
  hasBackButton?: boolean
  isDesktop?: boolean
}>`
  justify-content: ${({ hasBackButton }) => (hasBackButton ? 'space-between' : 'flex-end')};
  align-items: center;
  padding: ${({ theme }) => theme.spacing.m};
  background: inherit;
  width: 100%;
  top: 0;
  position: relative;

  ${props =>
    (props.enforceResponsiveMobileDialog || props.isDesktop) &&
    `
    padding: ${props.theme.spacing.l};
    padding-bottom: ${props.theme.spacing.xs};
    border-radius: ${props.theme.border.radius.large} ${props.theme.border.radius.large} 0 0;
  `}
`

export interface ModalHeaderProps {
  /**
   * Back button configuration.
   * When provided, a back button will be displayed.
   */
  backButtonProps?: {
    /**
     * Callback for back navigation.
     */
    onBack: () => void
    /**
     * Accessibility label for the back button.
     */
    ariaLabel: string
  }
  /**
   * Close button configuration.
   * When provided, a close button will be displayed.
   */
  closeButtonProps?: {
    /**
     * Callback when closed.
     */
    onClose: () => void
    /**
     * Accessibility label for the close button.
     */
    ariaLabel: string
  }
  enforceResponsiveMobileDialog?: boolean
  isDesktop?: boolean
  /**
   * Title in modal action header.
   */
  title?: string
}

const ArcadeModalHeader = ({
  backButtonProps,
  closeButtonProps,
  enforceResponsiveMobileDialog,
  isDesktop,
  title,
}: ModalHeaderProps) => {
  const { actionOverride } = useContext(ArcadeModalActionContext)

  if (actionOverride?.type === 'customHeader') {
    return actionOverride.CustomHeader
  }

  const { onBack, ariaLabel: ariaLabelBack } = { ...backButtonProps, ...actionOverride?.backButtonProps }
  const { onClose, ariaLabel: ariaLabelClose } = { ...closeButtonProps, ...actionOverride?.closeButtonProps }

  if (!onBack && !onClose) {
    return null
  }

  return (
    <ThemeVariantProvider themeVariant="monochrome">
      <ModalHeaderRow
        enforceResponsiveMobileDialog={enforceResponsiveMobileDialog}
        hasBackButton={!!onBack}
        isDesktop={isDesktop}
        data-testid="modal-header"
      >
        {title && (
          <HeaderTitle typography="titleBarPageTitle" color="textStandard" id="modal-header-title">
            {title}
          </HeaderTitle>
        )}
        {onBack && (
          <ArcadeButton
            size="icon"
            prominence="subtle"
            onClick={onBack}
            aria-label={ariaLabelBack}
            Icon={NavigationBackIcon as ArcadeIcon}
            data-testid="modal-back-button"
          />
        )}
        {onClose && (
          <ArcadeButton
            size="icon"
            prominence="subtle"
            onClick={onClose}
            aria-label={ariaLabelClose}
            Icon={NavigationCloseIcon as ArcadeIcon}
            data-testid="modal-close-button"
          />
        )}
      </ModalHeaderRow>
    </ThemeVariantProvider>
  )
}

export default ArcadeModalHeader
